import React, { useState } from 'react';
import styled from 'styled-components';
import CrosswordGrid from './CrosswordGrid';
import { puzzleAPI } from '../api';
import { colors, fonts, radius, shadow, sectionLabel, buttonPrimary, focusRing } from '../theme';

const BuilderContainer = styled.div`
  padding: 36px 20px 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 30px;
  text-align: center;
  margin: 0 0 28px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  ${sectionLabel}
  margin: 0 0 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 11px;
  margin-bottom: 10px;
  border: 1px solid ${colors.ruleStrong};
  border-radius: ${radius.sm};
  background: ${colors.card};
  color: ${colors.ink};
  font-size: 15px;

  &::placeholder {
    color: ${colors.inkFaint};
  }

  &:focus {
    outline: none;
    border-color: ${colors.accent};
  }
`;

const GridSection = styled.div`
  display: flex;
  gap: 30px;
  align-items: flex-start;
`;

const GridColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GridTitle = styled.h4`
  ${sectionLabel}
  margin: 0 0 12px;
`;

const CluesSection = styled.div`
  display: flex;
  gap: 30px;
`;

const ClueColumn = styled.div`
  flex: 1;
`;

const ClueInput = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ClueNumberSpan = styled.span`
  min-width: 26px;
  font-weight: 700;
  color: ${colors.inkFaint};
`;

const Button = styled.button`
  ${buttonPrimary}
  ${focusRing}
  margin-right: 10px;
`;

const SuccessMessage = styled.div`
  padding: 10px 14px;
  margin-bottom: 20px;
  border: 1px solid ${colors.success};
  border-radius: ${radius.sm};
  background: ${colors.successWash};
  color: ${colors.success};
  box-shadow: ${shadow.panel};
  text-align: center;
  font-size: 14px;
`;

export const CrosswordBuilder: React.FC = () => {
  const [puzzleTitle, setPuzzleTitle] = useState('');
  const [solutionGrid, setSolutionGrid] = useState<string[][]>(
    Array(5).fill(null).map(() => Array(5).fill(''))
  );
  const [acrossClues, setAcrossClues] = useState<{ [key: string]: string }>({});
  const [downClues, setDownClues] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSolutionChange = (row: number, col: number, value: string) => {
    const newGrid = [...solutionGrid];
    newGrid[row][col] = value === '#' ? '#' : value.toUpperCase();
    setSolutionGrid(newGrid);
  };

  const generateClueNumbers = () => {
    const numbers: { across: number[], down: number[] } = { across: [], down: [] };
    let currentNumber = 1;

    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 5; col++) {
        if (solutionGrid[row][col] !== '#') {
          const startsAcross = col === 0 || solutionGrid[row][col - 1] === '#';
          const startsDown = row === 0 || solutionGrid[row - 1][col] === '#';
          
          if (startsAcross || startsDown) {
            if (startsAcross) numbers.across.push(currentNumber);
            if (startsDown) numbers.down.push(currentNumber);
            currentNumber++;
          }
        }
      }
    }

    return numbers;
  };

  const clueNumbers = generateClueNumbers();

  const handleClueChange = (type: 'across' | 'down', number: number, value: string) => {
    if (type === 'across') {
      setAcrossClues(prev => ({ ...prev, [number]: value }));
    } else {
      setDownClues(prev => ({ ...prev, [number]: value }));
    }
  };

  const validatePuzzle = () => {
    if (!puzzleTitle.trim()) {
      alert('Please enter a puzzle title');
      return false;
    }

    // Check that all across clues have been filled
    for (const number of clueNumbers.across) {
      if (!acrossClues[number] || !acrossClues[number].trim()) {
        alert(`Please enter clue for ${number} Across`);
        return false;
      }
    }

    // Check that all down clues have been filled
    for (const number of clueNumbers.down) {
      if (!downClues[number] || !downClues[number].trim()) {
        alert(`Please enter clue for ${number} Down`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validatePuzzle()) return;

    setLoading(true);
    try {
      await puzzleAPI.createPuzzle({
        title: puzzleTitle,
        width: 5,
        height: 5,
        grid: Array(5).fill(null).map(() => Array(5).fill('')), // Empty grid for playing
        across_clues: acrossClues,
        down_clues: downClues,
        solution: solutionGrid,
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Reset form
      setPuzzleTitle('');
      setSolutionGrid(Array(5).fill(null).map(() => Array(5).fill('')));
      setAcrossClues({});
      setDownClues({});
    } catch (error) {
      alert('Error saving puzzle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BuilderContainer>
      <Title>Crossword Builder</Title>
      
      {success && (
        <SuccessMessage>
          Puzzle created successfully!
        </SuccessMessage>
      )}
      
      <Section>
        <Input
          type="text"
          placeholder="Puzzle Title"
          value={puzzleTitle}
          onChange={(e) => setPuzzleTitle(e.target.value)}
        />
      </Section>

      <Section>
        <SectionTitle>Create Your Grid</SectionTitle>
        <GridSection>
          <GridColumn>
            <GridTitle>Solution Grid</GridTitle>
            <CrosswordGrid
              grid={solutionGrid}
              solution={solutionGrid}
              onCellChange={handleSolutionChange}
              showNumbers={true}
              acrossClues={acrossClues}
              downClues={downClues}
            />
            <p style={{ fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
              Enter letters for answers, # for blocked cells
            </p>
          </GridColumn>
        </GridSection>
      </Section>

      <Section>
        <SectionTitle>Clues</SectionTitle>
        <CluesSection>
          <ClueColumn>
            <h4>Across</h4>
            {clueNumbers.across.map(number => (
              <ClueInput key={`across-${number}`}>
                <ClueNumberSpan>{number}.</ClueNumberSpan>
                <Input
                  type="text"
                  placeholder="Enter clue"
                  value={acrossClues[number] || ''}
                  onChange={(e) => handleClueChange('across', number, e.target.value)}
                />
              </ClueInput>
            ))}
          </ClueColumn>
          
          <ClueColumn>
            <h4>Down</h4>
            {clueNumbers.down.map(number => (
              <ClueInput key={`down-${number}`}>
                <ClueNumberSpan>{number}.</ClueNumberSpan>
                <Input
                  type="text"
                  placeholder="Enter clue"
                  value={downClues[number] || ''}
                  onChange={(e) => handleClueChange('down', number, e.target.value)}
                />
              </ClueInput>
            ))}
          </ClueColumn>
        </CluesSection>
      </Section>

      <Section>
        <Button onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Puzzle'}
        </Button>
      </Section>
    </BuilderContainer>
  );
};

export default CrosswordBuilder;